import { useState, useRef, useCallback } from 'react';

/**
 * CurrencyInput - Input monetário brasileiro com R$ embutido
 * 
 * Props:
 * - value: number (valor em reais, ex: 12.5)
 * - onChange: (number) => void
 * - className: string (classes extras para o container)
 * - inputClassName: string (classes extras para o input)
 * - disabled: boolean
 * - data-testid: string
 */
export const CurrencyInput = ({ 
  value = 0, 
  onChange, 
  className = '', 
  inputClassName = '',
  disabled = false,
  'data-testid': testId,
  ...rest 
}) => {
  const inputRef = useRef(null);
  
  // Formata número para exibição brasileira: 1234.5 -> "1.234,50"
  const formatBRL = useCallback((num) => {
    if (num === null || num === undefined || isNaN(num)) return '0,00';
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }, []);

  // Estado interno guarda o texto exibido
  const [displayValue, setDisplayValue] = useState(formatBRL(value));
  const [isFocused, setIsFocused] = useState(false);

  // Sincroniza com valor externo quando não está focado
  const currentDisplay = isFocused ? displayValue : formatBRL(value);

  const handleFocus = () => {
    setIsFocused(true);
    // Mostra valor formatado ao focar, seleciona tudo para facilitar edição
    setDisplayValue(formatBRL(value));
    setTimeout(() => {
      inputRef.current?.select();
    }, 10);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Parse do valor digitado para número
    const parsed = parseInputValue(displayValue);
    onChange?.(parsed);
    setDisplayValue(formatBRL(parsed));
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    
    // Permite apenas dígitos, vírgula e ponto
    const cleaned = raw.replace(/[^\d.,]/g, '');
    setDisplayValue(cleaned);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Parse: converte texto brasileiro para número
  // Aceita: "1.234,50" -> 1234.5, "12,5" -> 12.5, "12.5" -> 12.5
  const parseInputValue = (text) => {
    if (!text || text.trim() === '') return 0;
    
    let cleaned = text.trim();
    
    // Se tem vírgula, trata como separador decimal brasileiro
    if (cleaned.includes(',')) {
      // Remove pontos de milhar e troca vírgula por ponto
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }
    
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : Math.round(num * 100) / 100;
  };

  return (
    <div 
      className={`flex items-center h-11 border rounded-xl overflow-hidden transition-all ${
        isFocused 
          ? 'border-[#1B4332] ring-2 ring-[#1B4332]/10' 
          : 'border-[#E4E4E7] hover:border-[#A1A1AA]'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      data-testid={testId}
    >
      {/* Prefixo R$ fixo */}
      <span className="pl-3 pr-1.5 text-sm font-semibold text-[#52796F] select-none whitespace-nowrap">
        R$
      </span>
      
      {/* Input do valor */}
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={currentDisplay}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`flex-1 h-full bg-transparent outline-none text-sm text-[#18181B] font-medium pr-3 ${inputClassName}`}
        {...rest}
      />
    </div>
  );
};

export default CurrencyInput;
